import React, { useEffect, useState } from 'react';
import { getCountrylessRecords, scanCountrylessRecords, assignCountrylessRecord, getCountries, getCaseControlSettings, updateCaseControlSetting } from '../api/admin';

// System > Data Quality — "Without Country" review (Joint Architecture PDF,
// section 5). Assigning a country here now sets the user's REAL
// country_code -- the same field every other list (Users, Case Control,
// the country selector) actually filters on -- not just a tracking note.
// The original free-text value the user typed (e.g. "USA") is left exactly
// as it was: that is the brief's own "stays as history".
const DataQuality = () => {
  const [records, setRecords] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [assigning, setAssigning] = useState({}); // { [id]: countryCode }
  const [showAll, setShowAll] = useState(false);
  const [retentionMinutes, setRetentionMinutes] = useState(null);

  const load = () => { setLoading(true); getCountrylessRecords(showAll ? { status: 'all' } : {}).then((res) => setRecords(res.data?.records || [])).finally(() => setLoading(false)); };
  useEffect(() => { load(); getCountries().then((res) => setCountries(res.data?.countries || [])); }, [showAll]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    getCaseControlSettings().then((res) => {
      const row = (res.data?.settings || []).find((s2) => s2.key === 'data_quality_resolved_retention_minutes');
      if (row) setRetentionMinutes(row.value);
    });
  }, []);

  const saveRetention = async (value) => {
    await updateCaseControlSetting('data_quality_resolved_retention_minutes', value);
    setRetentionMinutes(value);
  };

  const runScan = async () => {
    setScanning(true);
    try {
      const res = await scanCountrylessRecords();
      setScanResult(res.data);
      load();
    } finally {
      setScanning(false);
    }
  };

  const assign = async (id) => {
    const code = assigning[id];
    if (!code) return;
    await assignCountrylessRecord(id, { assigned_country_code: code, status: 'assigned' });
    load();
  };

  const archive = async (id) => { await assignCountrylessRecord(id, { status: 'archived' }); load(); };

  return (
    <div style={s.page}>
      <h1 style={s.h1}>Data Quality — Without Country</h1>
      <p style={s.hint}>
        Scans user accounts for a country value that doesn't match an active country (for example
        a free-text city name, or no country at all). Assigning a country here sets the user's real
        country — they'll then appear in that country's lists throughout the system. The original
        text they typed (e.g. "USA") is kept exactly as it was, as a record of what it used to say.
      </p>

      <div style={s.actionsRow}>
        <button style={s.scanBtn} onClick={runScan} disabled={scanning}>{scanning ? 'Scanning…' : 'Scan now'}</button>
        <label style={s.toggleLabel}><input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> Show resolved / archived too</label>
        {retentionMinutes !== null && (
          <label style={s.toggleLabel}>
            Keep resolved items visible for
            <input style={s.minutesInput} type="number" min="1" defaultValue={retentionMinutes}
              onBlur={(e) => { if (e.target.value && e.target.value !== retentionMinutes) saveRetention(e.target.value); }} />
            minutes
          </label>
        )}
      </div>
      {scanResult && (
        <div style={s.scanNote}>
          Scanned {scanResult.scanned} users — {scanResult.flagged} currently unmatched, {scanResult.newly_added} newly added to this list.
        </div>
      )}

      {loading ? <p>Loading…</p> : (
        <table style={s.table}>
          <thead><tr>
            <th style={s.th}>Source</th><th style={s.th}>Reason</th><th style={s.th}>Detected</th>
            <th style={s.th}>Status</th><th style={s.th}>Assign country</th><th style={s.th}></th>
          </tr></thead>
          <tbody>
            {records.map((r) => (
              <tr key={r.id}>
                <td style={s.td}>{r.source_type} <span style={s.idTag}>{r.source_id.slice(0, 8)}</span></td>
                <td style={s.td}>{r.reason}</td>
                <td style={s.td}>{new Date(r.detected_at).toLocaleDateString()}</td>
                <td style={s.td}><span style={s.badge}>{r.status}{r.assigned_country_code ? ` → ${r.assigned_country_code}` : ''}</span></td>
                <td style={s.td}>
                  {r.status !== 'archived' && (
                    <select style={s.select} value={assigning[r.id] || ''} onChange={(e) => setAssigning((p) => ({ ...p, [r.id]: e.target.value }))}>
                      <option value="">Select…</option>
                      {countries.map((c) => <option key={c.country_code} value={c.country_code}>{c.country_name}</option>)}
                    </select>
                  )}
                </td>
                <td style={s.td}>
                  {r.status !== 'archived' && <>
                    <button style={s.btnGreen} onClick={() => assign(r.id)}>Assign</button>
                    <button style={s.btnGray} onClick={() => archive(r.id)}>Archive</button>
                  </>}
                </td>
              </tr>
            ))}
            {records.length === 0 && <tr><td style={s.td} colSpan={6}>No records flagged yet — run a scan to check.</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
};

const s = {
  page: { padding: 24, maxWidth: 1100 },
  h1: { fontSize: 22, fontWeight: 800, marginBottom: 8 },
  hint: { color: '#6b7280', fontSize: 13, marginBottom: 16, maxWidth: 700 },
  scanBtn: { padding: '8px 16px', borderRadius: 8, border: 'none', background: '#0B6A4A', color: 'white', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  actionsRow: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12, flexWrap: 'wrap' },
  toggleLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#374151' },
  minutesInput: { width: 56, padding: '4px 6px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12 },
  scanNote: { background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', padding: 10, borderRadius: 8, fontSize: 12.5, marginBottom: 16 },
  table: { width: '100%', borderCollapse: 'collapse', background: 'white', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: 12 },
  td: { padding: '8px 10px', borderBottom: '1px solid #f3f4f6' },
  idTag: { color: '#9ca3af', fontSize: 11 },
  badge: { padding: '3px 8px', borderRadius: 999, background: '#f3f4f6', fontSize: 11, fontWeight: 700 },
  select: { padding: '5px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12 },
  btnGreen: { padding: '5px 10px', marginRight: 4, borderRadius: 6, border: 'none', background: '#16a34a', color: 'white', cursor: 'pointer', fontSize: 12 },
  btnGray: { padding: '5px 10px', marginRight: 4, borderRadius: 6, border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer', fontSize: 12 },
};

export default DataQuality;
